import { useState } from "react";
import { CalendarRange, Pencil, Plus, Save, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { roleLabel } from "@/components/UserMenu";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const emptyTemplate = { name: "", owner_role: "hr", owner_id: "", schedule_kind: "monthly", day_of_month: 1, weekday: 1, due_time: "", priority: "Normal", lead_days: 7, notes: "", enabled: true };

export const describeSchedule = (t) => (t.schedule_kind === "monthly" ? `Monthly on the ${t.day_of_month}${["st", "nd", "rd"][((t.day_of_month % 10) - 1)] && t.day_of_month < 11 ? ["st", "nd", "rd"][(t.day_of_month % 10) - 1] : "th"}` : `Every ${WEEKDAYS[t.weekday]}`) + (t.due_time ? ` at ${t.due_time} ET` : "");

export const TemplatesPanel = ({ templates, owners, isAdmin, onChanged }) => {
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState("");
  const [error, setError] = useState("");

  async function save(event) {
    event.preventDefault();
    try {
      const payload = { ...editing, day_of_month: editing.schedule_kind === "monthly" ? Number(editing.day_of_month) : null, weekday: editing.schedule_kind === "weekly" ? Number(editing.weekday) : null, lead_days: Number(editing.lead_days) };
      if (editing.id) await api.put(`/tasks/templates/${editing.id}`, payload);
      else await api.post("/tasks/templates", payload);
      setEditing(null);
      setError("");
      await onChanged("Recurring template saved.");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function remove(template) {
    if (pendingDelete !== template.id) {
      setPendingDelete(template.id);
      return;
    }
    try {
      await api.delete(`/tasks/templates/${template.id}`);
      setPendingDelete("");
      await onChanged("Template deleted.");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  return (
    <section className="task-templates" data-testid="task-templates-panel">
      <div className="section-heading">
        <div><h3><CalendarRange size={16} /> Recurring tasks</h3><p>Templates generate tasks {"\u2264"} 7 days before each due date (never twice for the same date). {isAdmin ? "The System Administrator can edit them." : "Read-only."}</p></div>
        {isAdmin && <button type="button" className="button button-primary" data-testid="template-new-button" onClick={() => setEditing({ ...emptyTemplate })}><Plus size={14} /> New template</button>}
      </div>
      {error && <p className="form-message error-message" data-testid="template-error">{error}</p>}
      {editing && (
        <form className="task-form" data-testid="template-form" onSubmit={save}>
          <div className="task-form-grid">
            <label className="task-form-wide">Name<input data-testid="template-name-input" required minLength={3} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></label>
            <label>Owner role<select data-testid="template-owner-role" value={editing.owner_role} onChange={(e) => setEditing({ ...editing, owner_role: e.target.value })}><option value="hr">HR</option><option value="operations">Operations</option><option value="system_administrator">System Administrator</option></select></label>
            <label>Specific owner (optional)<select data-testid="template-owner-id" value={editing.owner_id || ""} onChange={(e) => setEditing({ ...editing, owner_id: e.target.value })}><option value="">First active user in the role</option>{owners.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select></label>
            <label>Schedule<select data-testid="template-schedule-kind" value={editing.schedule_kind} onChange={(e) => setEditing({ ...editing, schedule_kind: e.target.value })}><option value="monthly">Monthly</option><option value="weekly">Weekly</option></select></label>
            {editing.schedule_kind === "monthly" ? (
              <label>Day of month<input data-testid="template-day-of-month" type="number" min={1} max={28} value={editing.day_of_month ?? 1} onChange={(e) => setEditing({ ...editing, day_of_month: e.target.value })} /></label>
            ) : (
              <label>Weekday<select data-testid="template-weekday" value={editing.weekday ?? 1} onChange={(e) => setEditing({ ...editing, weekday: e.target.value })}>{WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}</select></label>
            )}
            <label>Due time (ET)<input data-testid="template-due-time" type="time" value={editing.due_time || ""} onChange={(e) => setEditing({ ...editing, due_time: e.target.value })} /></label>
            <label>Priority<select data-testid="template-priority" value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: e.target.value })}>{["High", "Normal", "Low"].map((p) => <option key={p}>{p}</option>)}</select></label>
            <label>Create days ahead<input data-testid="template-lead-days" type="number" min={0} max={30} value={editing.lead_days} onChange={(e) => setEditing({ ...editing, lead_days: e.target.value })} /></label>
            <label className="checkbox-row"><input data-testid="template-enabled" type="checkbox" checked={editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} /> Enabled</label>
            <label className="task-form-wide">Notes<textarea data-testid="template-notes" rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></label>
          </div>
          <div className="button-row">
            <button type="submit" className="button button-primary" data-testid="template-save"><Save size={14} /> Save template</button>
            <button type="button" className="button button-ghost" data-testid="template-cancel" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </form>
      )}
      <div className="credential-table-wrap">
        <table>
          <thead><tr><th>Template</th><th>Schedule</th><th>Owner</th><th>Priority</th><th>Enabled</th>{isAdmin && <th aria-label="Actions" />}</tr></thead>
          <tbody>
            {templates.map((t) => (
              <tr key={t.id} data-testid={`template-row-${t.id}`}>
                <td><b>{t.name}</b>{t.notes && <span>{t.notes}</span>}</td>
                <td>{describeSchedule(t)}</td>
                <td>{owners.find((o) => o.id === t.owner_id)?.name || roleLabel(t.owner_role)}</td>
                <td>{t.priority}</td>
                <td>{t.enabled ? "Yes" : "No"}</td>
                {isAdmin && <td className="credential-actions"><button type="button" className="table-edit-button" data-testid={`template-edit-${t.id}`} onClick={() => setEditing({ ...emptyTemplate, ...t })}><Pencil size={13} /> Edit</button><button type="button" className="table-edit-button table-delete-button" data-testid={`template-delete-${t.id}`} onClick={() => remove(t)}><Trash2 size={13} /> {pendingDelete === t.id ? "Confirm delete" : "Delete"}</button>{pendingDelete === t.id && <button type="button" className="table-edit-button" data-testid={`template-delete-cancel-${t.id}`} onClick={() => setPendingDelete("")}>Cancel</button>}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export const SprintsPanel = ({ sprints, onChanged, onSelect, selected }) => {
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "" });
  const [error, setError] = useState("");

  async function create(event) {
    event.preventDefault();
    try {
      await api.post("/tasks/sprints", form);
      setForm({ name: "", start_date: "", end_date: "" });
      setError("");
      await onChanged("Sprint created.");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  return (
    <section className="task-sprints" data-testid="task-sprints-panel">
      <div className="section-heading"><div><h3>Sprints</h3><p>Optional grouping with start and end dates; completion counts verified tasks.</p></div></div>
      <form className="task-sprint-form" data-testid="sprint-form" onSubmit={create}>
        <input data-testid="sprint-name-input" required minLength={2} placeholder="Sprint name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input data-testid="sprint-start-input" required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
        <input data-testid="sprint-end-input" required type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
        <button type="submit" className="button button-primary" data-testid="sprint-create"><Plus size={14} /> Add sprint</button>
      </form>
      {error && <p className="form-message error-message" data-testid="sprint-error">{error}</p>}
      <div className="task-sprint-list">
        {sprints.map((s) => (
          <button type="button" key={s.id} className={`task-sprint-card ${selected === s.id ? "task-sprint-card-active" : ""}`} data-testid={`sprint-card-${s.id}`} onClick={() => onSelect(selected === s.id ? "" : s.id)}>
            <strong>{s.name}</strong>
            <small>{s.start_date} → {s.end_date}</small>
            <div className="progress-track"><span style={{ width: `${s.completion_percent}%`, background: "#0D5C59" }} /></div>
            <small data-testid={`sprint-progress-${s.id}`}>{s.completion_percent}% complete · {s.verified_count}/{s.task_count} verified</small>
          </button>
        ))}
        {!sprints.length && <p className="pairing-meta">No sprints yet.</p>}
      </div>
    </section>
  );
};
